import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import ChatMarkdown from '../components/ChatMarkdown';
import { ASSISTANT_BUBBLE, USER_BUBBLE } from '../components/chat/bubbles';
import { api } from '../lib/api';

interface Conversation {
  id: string;
  type: 'deal' | 'app';
  title: string;
  property_name: string | null;
  property_id: string | null;
  page_context: string | null;
  created_at: string;
  updated_at: string;
}

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  attachments?: Array<{ file_name: string; file_size: number; mime_type: string; sendable: boolean }>;
  suggestion_data?: { suggestions: any[] };
  starred?: boolean;
  created_at: string;
}

type FilterType = 'all' | 'deal' | 'app';

/**
 * The list/detail breakpoint. Kept in step with the lg: classes on the two panes below -
 * 1024px is Tailwind's lg, and this query exists only to tell JS which of those two branches
 * is live. A phone cannot show both panes: at 375px the page has roughly 343px of usable
 * width, and the list alone wanted w-80 (320px) + gap-6 (24px) = 344px while being
 * flex-shrink-0, which squeezed the detail pane into an unreadable sliver off the right edge.
 */
const LG_QUERY = '(min-width: 1024px)';

export default function AIConversations() {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<FilterType>('all');
  const [search, setSearch] = useState('');
  const [selectedChat, setSelectedChat] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [loadingMessages, setLoadingMessages] = useState(false);

  // ⚠️ Read in useState's initialiser, NOT in an effect. This decides WHICH PANE RENDERS, so
  // an effect-only read would paint the one-pane phone layout for a frame on every desktop
  // load. Where matchMedia does not exist at all (jsdom, very old browsers) the two-pane
  // layout wins: that is the layout already verified working, so an unknown environment gets
  // the known-good one. (AiRail defaults the other way because there `atXl` only tunes an
  // overlay that CSS has already placed - here it chooses the markup.)
  const [atLg, setAtLg] = useState(
    () => (typeof window.matchMedia === 'function' ? window.matchMedia(LG_QUERY).matches : true)
  );

  const loadConversations = useCallback(async () => {
    setLoading(true);
    try {
      const typeParam = filter === 'all' ? undefined : filter;
      const res = await api.aiConversations(typeParam);
      setConversations(res.conversations || []);
    } catch (err) {
      console.error('Failed to load conversations:', err);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  // matchMedia rather than an innerWidth read, so a rotation or a resized window arrives as a
  // change event instead of being missed. Same shape as AiRail's XL_QUERY subscription.
  useEffect(() => {
    const mq = typeof window.matchMedia === 'function' ? window.matchMedia(LG_QUERY) : null;
    if (!mq) return;
    setAtLg(mq.matches);
    const onChange = (e: MediaQueryListEvent) => setAtLg(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const loadMessages = async (conv: Conversation) => {
    setSelectedChat(conv);
    setLoadingMessages(true);
    try {
      const res = await api.aiConversationMessages(conv.type, conv.id);
      setMessages(res.messages || []);
    } catch (err) {
      console.error('Failed to load messages:', err);
    } finally {
      setLoadingMessages(false);
    }
  };

  const filteredConversations = search
    ? conversations.filter(
        (c) =>
          c.title?.toLowerCase().includes(search.toLowerCase()) ||
          c.property_name?.toLowerCase().includes(search.toLowerCase())
      )
    : conversations;

  // Below lg exactly ONE pane is on screen: the list until a conversation is picked, then the
  // detail with a Back control that returns to the list. At lg and above both render, exactly
  // as they did before - `selectedChat` is the single piece of state driving both.
  const showList = atLg || !selectedChat;
  const showDetail = atLg || !!selectedChat;

  return (
    <Layout>
      <div className="space-y-6">
        {/* Page header */}
        <div>
          <h1 className="font-display text-[28px] font-semibold uppercase leading-[1.06] tracking-[-.01em] text-gray-900 sm:text-[32px]">AI Conversations</h1>
          <p className="mt-1 text-sm text-gray-500">
            All your AI chats in one place — Deal Advisor and App Assistant conversations
          </p>
        </div>

        {/*
          🚨 dvh, NOT vh. On iOS Safari `vh` resolves against the LARGE viewport - the one that
          ignores the browser chrome - so a 100vh-derived height is taller than what is actually
          visible and the bottom of the pane is cut off behind the toolbar. The AI rail already
          uses dvh for this reason.
        */}
        <div className="flex flex-col lg:flex-row gap-6 h-[calc(var(--vh-full)-220px-var(--tab-bar-space,0px))] min-h-[500px]">
          {/* Left panel: Conversation list */}
          {showList && (
          // w-full + flex-1 below lg (fills the column), w-80 + flex-none at lg (identical to
          // the w-80 flex-shrink-0 this replaces). min-w-0 so a long title truncates instead of
          // widening the pane past the viewport.
          <div className="w-full flex-1 min-w-0 lg:w-80 lg:flex-none bg-white rounded-xl border border-gray-200 flex flex-col overflow-hidden">
            {/* Filter tabs */}
            <div className="flex border-b border-gray-200">
              {([
                { key: 'all', label: 'All' },
                { key: 'deal', label: 'Deal Advisor' },
                { key: 'app', label: 'App Assistant' },
              ] as const).map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setFilter(tab.key)}
                  className={`flex-1 px-3 py-2.5 text-xs font-medium transition-colors ${
                    filter === tab.key
                      ? 'text-primary-500 border-b-2 border-primary-500 bg-primary-50/50'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Search */}
            <div className="p-3 border-b border-gray-100">
              <div className="relative">
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  placeholder="Search conversations..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Conversation list */}
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full" />
                </div>
              ) : filteredConversations.length === 0 ? (
                <div className="text-center text-gray-400 mt-12 px-4">
                  <svg className="w-10 h-10 mx-auto mb-2 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  <p className="text-sm">No conversations yet</p>
                </div>
              ) : (
                filteredConversations.map((conv) => (
                  <button
                    key={`${conv.type}-${conv.id}`}
                    onClick={() => loadMessages(conv)}
                    className={`w-full text-left px-4 py-3 border-b border-gray-50 hover:bg-gray-50 transition-colors ${
                      selectedChat?.id === conv.id && selectedChat?.type === conv.type
                        ? 'bg-primary-50 border-l-2 border-l-primary-600'
                        : ''
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      {/* Type badge */}
                      <span className={`mt-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium flex-shrink-0 ${
                        conv.type === 'deal'
                          ? 'bg-info-wash text-info'
                          : 'bg-success-wash text-success'
                      }`}>
                        {conv.type === 'deal' ? 'Deal' : 'App'}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-800 truncate">
                          {conv.title || 'Untitled'}
                        </div>
                        {conv.property_name && (
                          <div className="text-xs text-info truncate mt-0.5">
                            {conv.property_name}
                          </div>
                        )}
                        <div className="text-xs text-gray-400 mt-0.5">
                          {new Date(conv.updated_at).toLocaleDateString(undefined, {
                            month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
                          })}
                        </div>
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
          )}

          {/* Right panel: Message viewer */}
          {showDetail && (
          <div className="w-full flex-1 min-w-0 lg:w-auto bg-white rounded-xl border border-gray-200 flex flex-col overflow-hidden">
            {!selectedChat ? (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center text-gray-400">
                  <svg className="w-16 h-16 mx-auto mb-4 text-gray-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456z" />
                  </svg>
                  <p className="text-lg font-medium text-gray-500">Select a conversation</p>
                  <p className="text-sm mt-1">Pick a chat from the left to view messages</p>
                </div>
              </div>
            ) : (
              <>
                {/* Chat header */}
                <div className="px-4 py-3 sm:px-5 sm:py-4 border-b border-gray-200 flex items-center gap-2 sm:gap-3">
                  {!atLg && (
                    // min-h/min-w 44px: Apple's minimum tap target, same rule the rail header
                    // follows. -ml-2 pulls the enlarged hit area back to the pane's edge so the
                    // label still lines up with the content below it.
                    <button
                      onClick={() => setSelectedChat(null)}
                      aria-label="Back to conversations"
                      className="-ml-2 min-h-[44px] min-w-[44px] flex-shrink-0 inline-flex items-center justify-center gap-1 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                      Back
                    </button>
                  )}
                  <span className={`px-2 py-0.5 rounded text-xs font-medium flex-shrink-0 ${
                    selectedChat.type === 'deal'
                      ? 'bg-info-wash text-info'
                      : 'bg-success-wash text-success'
                  }`}>
                    {selectedChat.type === 'deal' ? 'Deal Advisor' : 'App Assistant'}
                  </span>
                  <div className="flex-1 min-w-0">
                    <h2 className="font-semibold text-gray-900 truncate">{selectedChat.title || 'Untitled'}</h2>
                    {selectedChat.property_name && (
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-xs text-gray-500">{selectedChat.property_name}</span>
                        {selectedChat.property_id && (
                          <Link
                            to={`/properties/${selectedChat.property_id}`}
                            className="text-xs text-info hover:text-info font-medium"
                          >
                            View Property
                          </Link>
                        )}
                      </div>
                    )}
                  </div>
                  {/* Hidden below sm: at 375px the Back control, the badge and the title already
                      fill the row, and every conversation in the list carries its own date. */}
                  <div className="hidden sm:block flex-shrink-0 text-xs text-gray-400">
                    {new Date(selectedChat.updated_at).toLocaleDateString(undefined, {
                      month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit',
                    })}
                  </div>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-5 space-y-4">
                  {loadingMessages ? (
                    <div className="flex items-center justify-center h-32">
                      <div className="animate-spin w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full" />
                    </div>
                  ) : messages.length === 0 ? (
                    <div className="text-center text-gray-400 mt-10">
                      <p className="text-sm">No messages in this conversation</p>
                    </div>
                  ) : (
                    messages.map((msg) => (
                      <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                        {/* CHAT-DUET (his ask, 2026-10-29): the chat's own bubbles (`bubbles.ts`) - his soft Apex red, the
                            assistant's light grey, even 20px corners, 17px on a phone and 15px from sm - as in the Assistant. */}
                        <div
                          data-bubble={msg.role === 'user' ? 'user' : 'assistant'}
                          className={`max-w-[85%] lg:max-w-[75%] ${
                            msg.role === 'user' ? `whitespace-pre-wrap ${USER_BUBBLE}` : ASSISTANT_BUBBLE
                          }`}
                        >
                          {/* ⛔ ASSISTANT ONLY. The local hand-rolled renderer this replaces
                              handled code fences, **bold** and `inline code` and NOTHING else,
                              so a markdown TABLE printed as raw pipes on his phone. ChatMarkdown
                              keeps all three of those and adds the table. A message Joseph typed
                              himself still renders exactly as it did - literally. */}
                          <div>
                            {msg.role === 'assistant' ? <ChatMarkdown text={msg.content} /> : msg.content}
                          </div>

                          {/* Attachment badges */}
                          {msg.attachments && msg.attachments.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {msg.attachments.map((att, i) => (
                                <span key={i} className="inline-flex items-center gap-1 rounded bg-white/70 px-2 py-0.5 text-[10px] text-gray-600">
                                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                                  </svg>
                                  {att.file_name}
                                </span>
                              ))}
                            </div>
                          )}

                          {/* Timestamp */}
                          <div className="mt-1.5 text-[11px] text-gray-500">
                            {new Date(msg.created_at).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </>
            )}
          </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
